import { Asset, AssetsDebts, ConsentOrdersBase, Debt, InitialDetails } from "@fla/components";
import { ActionReducerMapBuilder, createAction, createSlice } from "@reduxjs/toolkit";
import { SliceStatus } from "interfaces";

import {
  addAsset,
  addDebt,
  setAssets,
  setAssetsDebts,
  setAssetsDebtsStep,
  setDebts,
  setInitialDetailsStep,
  updateAsset,
  updateDebt
} from "./consentOrdersActions";

const setBase = createAction<ConsentOrdersBase>("setBase");

type consentOrdersSliceState = {
  status: SliceStatus;
  error: string;

  base: ConsentOrdersBase;

  initialDetailsStep: { passedSteps: number[]; currentStep: number };
  assetsDebtsStep: { passedSteps: number[]; currentStep: number };

  assets: Asset[];
  debts: Debt[];
};

const initialState = {
  status: SliceStatus.Idle,
  error: "",

  base: {
    assetsDebts: {} as AssetsDebts,
    initialDetails: { isCompleted: false, currentStep: 1 } as InitialDetails,
    submitted: false,
    uid: ""
  },

  initialDetailsStep: {
    passedSteps: [],
    currentStep: 1
  },
  assetsDebtsStep: {
    passedSteps: [],
    currentStep: 1
  },

  assets: [] as Asset[],
  debts: [] as Debt[]
};

const consentOrdersSlice = createSlice({
  name: "consentOrders",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<consentOrdersSliceState>) =>
    builder
      .addCase(setAssets, (state, action) => ({
        ...state,
        assets: action.payload
      }))
      .addCase(addAsset, (state, action) => ({
        ...state,
        assets: [action.payload, ...state.assets]
      }))
      .addCase(updateAsset, (state, action) => {
        const index = state.assets.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.assets[index] = action.payload;
        }
      })
      .addCase(setDebts, (state, action) => ({
        ...state,
        debts: action.payload
      }))
      .addCase(addDebt, (state, action) => ({
        ...state,
        debts: [action.payload, ...state.debts]
      }))
      .addCase(updateDebt, (state, action) => {
        const index = state.debts.findIndex(v => v.id === action.payload.id);
        if (index !== -1) {
          state.debts[index] = action.payload;
        }
      })
      .addCase(setAssetsDebts, (state, action) => ({
        ...state,
        base: {
          ...state.base,
          assetsDebts: {
            ...state.base.assetsDebts,
            ...action.payload
          }
        }
      }))
      .addCase(setInitialDetailsStep, (state, action) => ({
        ...state,
        initialDetailsStep: {
          ...state.initialDetailsStep,
          ...action.payload
        }
      }))

      .addCase(setAssetsDebtsStep, (state, action) => ({
        ...state,
        assetsDebtsStep: {
          ...state.assetsDebtsStep,
          ...action.payload
        }
      }))
      .addCase(setBase, (state, action) => ({
        ...state,
        base: {
          ...state.base,
          ...action.payload
        }
      }))
      .addDefaultCase(state => state)
});

export const { reducer: consentOrdersReducer, name: consentOrdersReducerName } = consentOrdersSlice;

export type TConsentOrdersActions =
  | ReturnType<typeof setInitialDetailsStep>
  | ReturnType<typeof setAssets>
  | ReturnType<typeof addAsset>
  | ReturnType<typeof updateAsset>
  | ReturnType<typeof setDebts>
  | ReturnType<typeof addDebt>
  | ReturnType<typeof updateDebt>
  | ReturnType<typeof setAssetsDebts>;

export const consentOrdersActions = {
  addAsset,
  setAssets,
  updateAsset,
  addDebt,
  setDebts,
  updateDebt,
  setAssetsDebtsStep,
  setInitialDetailsStep,
  setAssetsDebts,
  setBase
};
export type ConsentOrdersState = ReturnType<typeof consentOrdersReducer>;
